import { useState, useMemo, useCallback } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import { ChevronLeft, ChevronRight, AlertTriangle, ArrowRight, Camera, Pencil, SlidersHorizontal } from 'lucide-react';
import { Link } from 'react-router-dom';
import { useCashFlow } from '../hooks/useCashFlow.js';
import { useForecastTree } from '../hooks/useForecastTree.js';
import { useRevenueHypothesis } from '../hooks/useRevenueHypothesis.js';
import CashFlowChart from '../components/CashFlowChart.jsx';
import CashFlowHeaderKPIs from '../components/CashFlowHeaderKPIs.jsx';
import ActualVsBudgetTable from '../components/ActualVsBudgetTable.jsx';
import CashFlowMonthView from '../components/cashflow/CashFlowMonthView.jsx';
import { useMediaQuery } from '../hooks/useMediaQuery.js';
import PeriodCategoryPanel from '../components/PeriodCategoryPanel.jsx';
import DebtsSummaryWidget from '../components/DebtsSummaryWidget.jsx';
import GranularityToggle, { suggestGranularity } from '../components/GranularityToggle.jsx';
import ScenarioSwitcher from '../components/forecasts/ScenarioSwitcher.jsx';
import LoadingSpinner from '../components/LoadingSpinner.jsx';
import SnapshotAdminModal from '../components/cashflow/SnapshotAdminModal.jsx';
import { formatCurrency } from '../lib/format.js';

// ── Date helpers (no external lib — keep consistent with existing codebase) ──

function toISO(date) {
  return date.toISOString().slice(0, 10);
}

function addMonths(date, n) {
  const d = new Date(date);
  d.setMonth(d.getMonth() + n);
  return d;
}

function diffDays(from, to) {
  return Math.round((new Date(to) - new Date(from)) / 86400000);
}

function currentYearPeriod() {
  const y = new Date().getFullYear();
  return { from: `${y}-01-01`, to: `${y}-12-31` };
}

function extendedPeriod() {
  const today = new Date();
  today.setHours(0, 0, 0, 0);
  return { from: toISO(addMonths(today, -12)), to: toISO(addMonths(today, 12)) };
}

// ── ViewMode toggle ───────────────────────────────────────────────────────────

const VIEW_MODES = [
  { value: 'all', label: 'Tout' },
  { value: 'actual', label: 'Réel' },
  { value: 'forecast', label: 'Prévisionnel' },
  { value: 'budget', label: 'vs Budget' },
];

function ViewModeToggle({ value, onChange }) {
  return (
    <div className="flex rounded-lg border border-slate-200 overflow-hidden text-xs" role="group" aria-label="Mode d'affichage">
      {VIEW_MODES.map((m) => (
        <button
          key={m.value}
          onClick={() => onChange(m.value)}
          aria-pressed={value === m.value}
          className={`px-3 py-1.5 font-medium transition-colors ${
            value === m.value
              ? 'bg-slate-800 text-white'
              : 'bg-white text-slate-600 hover:bg-slate-50'
          }`}
        >
          {m.label}
        </button>
      ))}
    </div>
  );
}

// ── Range toggle ─────────────────────────────────────────────────────────────

const RANGE_MODES = [
  { value: 'currentYear', label: 'Année en cours' },
  { value: 'extended',    label: 'M±12' },
];

function RangeToggle({ value, onChange }) {
  return (
    <div className="flex rounded-lg border border-slate-200 overflow-hidden text-xs" role="group" aria-label="Plage temporelle">
      {RANGE_MODES.map((m) => (
        <button
          key={m.value}
          onClick={() => onChange(m.value)}
          aria-pressed={value === m.value}
          className={`px-3 py-1.5 font-medium transition-colors ${
            value === m.value ? 'bg-slate-800 text-white' : 'bg-white text-slate-600 hover:bg-slate-50'
          }`}
        >
          {m.label}
        </button>
      ))}
    </div>
  );
}

// ── Period nav ────────────────────────────────────────────────────────────────

function PeriodNav({ period, onChange }) {
  function shift(direction) {
    const days = diffDays(period.from, period.to);
    const months = Math.round(days / 30);
    onChange({
      from: toISO(addMonths(new Date(period.from), direction * months)),
      to: toISO(addMonths(new Date(period.to), direction * months)),
    });
  }

  const fromLabel = new Date(period.from + 'T00:00:00').toLocaleDateString('fr-FR', {
    day: '2-digit', month: 'short', year: '2-digit',
  });
  const toLabel = new Date(period.to + 'T00:00:00').toLocaleDateString('fr-FR', {
    day: '2-digit', month: 'short', year: '2-digit',
  });

  return (
    <div className="flex items-center gap-1">
      <button
        onClick={() => shift(-1)}
        className="p-1.5 rounded-lg border border-slate-200 hover:bg-slate-50 transition-colors"
        aria-label="Période précédente"
      >
        <ChevronLeft size={16} />
      </button>
      <span className="text-xs font-medium text-slate-700 min-w-[148px] text-center">
        {fromLabel} → {toLabel}
      </span>
      <button
        onClick={() => shift(1)}
        className="p-1.5 rounded-lg border border-slate-200 hover:bg-slate-50 transition-colors"
        aria-label="Période suivante"
      >
        <ChevronRight size={16} />
      </button>
    </div>
  );
}

// ── Provisions summary banner ─────────────────────────────────────────────────

/**
 * Collecte les provisions actives depuis l'arbre forecast et calcule le total
 * disponible. Affiche un bandeau compact avec lien vers /previsionnels.
 */
function ProvisionsSummaryBanner({ forecastData }) {
  // L'arbre forecast expose : { flows: [{ flow_type, categories: [{ items: [...] }] }] }
  const provisions = useMemo(() => {
    if (!forecastData?.flows) return [];
    const result = [];
    for (const flow of forecastData.flows) {
      for (const cat of flow.categories ?? []) {
        for (const item of cat.items ?? []) {
          if (item.mode === 'provision' && item.status === 'active') {
            result.push(item);
          }
          // Recurse into children categories
          for (const child of cat.children ?? []) {
            for (const childItem of child.items ?? []) {
              if (childItem.mode === 'provision' && childItem.status === 'active') {
                result.push(childItem);
              }
            }
          }
        }
      }
    }
    return result;
  }, [forecastData]);

  if (provisions.length === 0) return null;

  // balance_cents est exposé dans l'état provision via useForecastTree
  // quand le backend renvoie state.balance_cents dans les items provision.
  // Si absent, on affiche seulement le nombre de provisions.
  const totalBalanceCents = provisions.reduce((sum, p) => {
    const bal = p.state?.balance_cents ?? p.balance_cents ?? null;
    return bal != null ? sum + Number(bal) : sum;
  }, 0);

  const hasBalance = provisions.some(
    (p) => p.state?.balance_cents != null || p.balance_cents != null
  );

  return (
    <div className="flex items-center justify-between gap-3 rounded-lg bg-indigo-50 border border-indigo-200 px-3 py-2.5 text-sm">
      <div className="flex items-start gap-2 min-w-0">
        <AlertTriangle size={15} className="shrink-0 mt-0.5 text-indigo-500" aria-hidden="true" />
        <span className="text-indigo-800 leading-snug">
          <span className="font-semibold">
            {provisions.length} provision{provisions.length > 1 ? 's' : ''} active{provisions.length > 1 ? 's' : ''}
          </span>
          {hasBalance && (
            <>
              {' '}—{' '}
              <span className="font-semibold">{formatCurrency(totalBalanceCents)}</span>
              {' '}disponible{provisions.length > 1 ? 's' : ''} au total
            </>
          )}
        </span>
      </div>
      <Link
        to="/previsionnels"
        className="shrink-0 flex items-center gap-1 text-xs font-medium text-indigo-600 hover:text-indigo-800 transition-colors whitespace-nowrap"
        aria-label="Gérer les provisions sur la page Prévisionnels"
      >
        Gérer
        <ArrowRight size={12} />
      </Link>
    </div>
  );
}

const PHONE_PORTRAIT_QUERY = '(max-width: 767px) and (orientation: portrait)';

// ── Page ──────────────────────────────────────────────────────────────────────

export default function CashFlowPage() {
  const isPhonePortrait = useMediaQuery(PHONE_PORTRAIT_QUERY);
  const [optionsOpen, setOptionsOpen] = useState(false);
  const showOptions = !isPhonePortrait || optionsOpen;
  const todayISO = toISO(new Date());

  const [rangeMode, setRangeMode] = useState('currentYear');
  const [period, setPeriod] = useState(currentYearPeriod);
  const [granularity, setGranularity] = useState(() =>
    suggestGranularity(diffDays(currentYearPeriod().from, currentYearPeriod().to))
  );
  const [viewMode, setViewMode] = useState('all');
  const [scenario, setScenario] = useState('median');
  const [editingTable, setEditingTable] = useState(false);
  const [snapshotModalOpen, setSnapshotModalOpen] = useState(false);

  function handleRangeModeChange(mode) {
    setRangeMode(mode);
    setPeriod(mode === 'currentYear' ? currentYearPeriod() : extendedPeriod());
  }

  const queryClient = useQueryClient();

  // Drill-down cellule : { from, to, categoryIds, mode, title, subtitle, flowType, scenario, unclassified?, vatFilter? } | null
  const [cellDetail, setCellDetail] = useState(null);
  const handleCellClick = useCallback(
    (detail) => setCellDetail(detail ? { ...detail, scenario } : null),
    [scenario]
  );

  // Drill-down sur ligne TVA : ouvre le panel avec vat_filter (collected/deductible)
  // côté = 'actuals' (réel) ou 'forecasts' (prévi).
  const handleVatCellClick = useCallback(
    (col, direction, side) => {
      const directionLabel = direction === 'collected' ? 'TVA collectée' : 'TVA déductible';
      const sideLabel      = side === 'actuals' ? 'réel' : 'prévi';
      setCellDetail({
        from:        col.period_start,
        to:          col.period_end,
        categoryIds: [],
        mode:        side === 'actuals' ? 'actuals' : 'forecasts',
        title:       directionLabel,
        subtitle:    `${col.shortLabel ?? col.period_start} — ${sideLabel}`,
        flowType:    direction === 'collected' ? 'income' : 'expense',
        scenario,
        vatFilter:   direction,
      });
    },
    [scenario]
  );

  const handlePanelClose = useCallback(() => {
    setCellDetail(null);
    // Après recatégorisation, recalculer unclassified_actual_cents dans le tableau
    queryClient.invalidateQueries({ queryKey: ['cash-flow'] });
  }, [queryClient]);

  const periodDays = useMemo(
    () => diffDays(period.from, period.to),
    [period.from, period.to]
  );

  // Auto-suggest granularity when period changes, but only if user hasn't overridden
  // (We track this by only suggesting, not forcing — the toggle does the forcing)

  const { data, isLoading, error } = useCashFlow({
    from: period.from,
    to: period.to,
    granularity,
    scenario,
  });

  // Provisions summary — chargé en parallèle, erreur silencieuse (page CF reste fonctionnelle)
  const { data: forecastTreeData } = useForecastTree();

  // Hypothèse de revenus — pour afficher le CA estimé sous le switcher de scénario
  const { data: hypothesis } = useRevenueHypothesis();

  // Compte les provisions actives pour la note de légende du chart
  const activeProvisionCount = useMemo(() => {
    if (!forecastTreeData?.flows) return 0;
    let count = 0;
    for (const flow of forecastTreeData.flows) {
      for (const cat of flow.categories ?? []) {
        for (const item of cat.items ?? []) {
          if (item.mode === 'provision' && item.status === 'active') count++;
        }
        for (const child of cat.children ?? []) {
          for (const item of child.items ?? []) {
            if (item.mode === 'provision' && item.status === 'active') count++;
          }
        }
      }
    }
    return count;
  }, [forecastTreeData]);

  // CA estimé moyen sur la fenêtre active (approximation mensuelle depuis l'hypothèse)
  // Formule : baseline × varMul × growthMul_avg (une valeur représentative, pas mois par mois)
  const estimatedMonthlyCents = useMemo(() => {
    if (!hypothesis) return null;
    const {
      baseline_cents_monthly,
      growth_pct_yearly = 0,
      pessimiste_variance_pct = -15,
      optimiste_variance_pct = 15,
      start_year = 2026,
      seasonality_coefs,
    } = hypothesis;

    // Multiplicateur de variance selon le scénario
    const varMul =
      scenario === 'pessimiste'
        ? 1 + pessimiste_variance_pct / 100
        : scenario === 'optimiste'
        ? 1 + optimiste_variance_pct / 100
        : 1;

    // Années écoulées depuis start_year (milieu de la fenêtre visible)
    const midDate = new Date(
      (new Date(period.from).getTime() + new Date(period.to).getTime()) / 2
    );
    const yearsOffset = midDate.getFullYear() - start_year;
    const growthMul = Math.pow(1 + growth_pct_yearly / 100, Math.max(0, yearsOffset));

    // Coef de saisonnalité moyen sur la fenêtre (moyenne des coefs des mois concernés)
    let seasonMul = 1;
    if (Array.isArray(seasonality_coefs) && seasonality_coefs.length === 12) {
      const fromD = new Date(period.from + 'T00:00:00');
      const toD = new Date(period.to + 'T00:00:00');
      const months = [];
      const cur = new Date(fromD.getFullYear(), fromD.getMonth(), 1);
      while (cur <= toD) {
        months.push(cur.getMonth()); // 0-indexed
        cur.setMonth(cur.getMonth() + 1);
      }
      if (months.length > 0) {
        seasonMul =
          months.reduce((s, m) => s + (seasonality_coefs[m] ?? 1), 0) / months.length;
      }
    }

    return Math.round(baseline_cents_monthly * seasonMul * varMul * growthMul);
  }, [hypothesis, scenario, period]);

  const buckets = data?.buckets ?? [];
  const byCategory = data?.by_category ?? [];
  const today = data?.today ?? todayISO;

  const isEmpty = !isLoading && !error && buckets.length === 0;

  return (
    <div className="flex flex-col gap-5">
      {/* Header — masqué en paysage téléphone : la hauteur va au tableau */}
      <div className="phonels:hidden">
        <h1 className="text-lg font-semibold text-slate-900">Cash-flow</h1>
        <p className="text-sm text-slate-500">Trajectoire de solde et projections</p>
      </div>

      {/* Provisions summary banner — conditionnel sur la présence de provisions actives */}
      <ProvisionsSummaryBanner forecastData={forecastTreeData} />

      {/* Toolbar */}
      <div className="space-y-2">
        <div className="flex flex-wrap gap-2 md:gap-3 items-center">
          <RangeToggle value={rangeMode} onChange={handleRangeModeChange} />
          <PeriodNav period={period} onChange={setPeriod} />
          <GranularityToggle
            value={granularity}
            onChange={setGranularity}
            periodDays={periodDays}
          />
          {isPhonePortrait && (
            <button
              type="button"
              onClick={() => setOptionsOpen((o) => !o)}
              aria-expanded={optionsOpen}
              className={`flex min-h-9 items-center gap-1.5 rounded-lg border px-2.5 text-xs transition-colors ${
                optionsOpen ? 'border-slate-800 bg-slate-800 text-white' : 'border-slate-200 text-slate-600'
              }`}
            >
              <SlidersHorizontal size={14} aria-hidden />
              Options
            </button>
          )}
          {showOptions && <ViewModeToggle value={viewMode} onChange={setViewMode} />}
          {showOptions && <ScenarioSwitcher value={scenario} onChange={setScenario} />}
          {!isPhonePortrait && (
            <button
              type="button"
              onClick={() => setEditingTable(true)}
              aria-label="Réorganiser le tableau"
              className="flex items-center gap-1.5 text-xs text-slate-600 hover:text-blue-600 px-2 py-1.5 rounded border border-slate-200 hover:bg-blue-50 transition-colors"
            >
              <Pencil size={14} />
              <span className="hidden sm:inline">Réorganiser</span>
            </button>
          )}
          {showOptions && <button
            type="button"
            onClick={() => setSnapshotModalOpen(true)}
            aria-label="Gérer les snapshots cash-flow"
            className="p-1.5 rounded-lg border border-slate-200 hover:bg-slate-50 text-slate-500 hover:text-slate-700 transition-colors"
            title="Snapshots cash-flow"
          >
            <Camera size={16} />
          </button>}
        </div>

        {/* CA estimé sous le switcher */}
        <div className="text-xs text-slate-500 pl-0.5">
          {estimatedMonthlyCents != null ? (
            <>
              CA estimé :{' '}
              <span className="font-medium text-slate-700">
                {formatCurrency(estimatedMonthlyCents)}/mois
              </span>{' '}
              en moyenne sur la fenêtre (scénario {scenario})
            </>
          ) : (
            <>
              Aucune hypothèse de revenus configurée —{' '}
              <Link
                to="/previsionnels"
                className="text-indigo-600 hover:text-indigo-800 underline underline-offset-2"
              >
                Configurer dans /prévisionnels
              </Link>
            </>
          )}
        </div>
      </div>

      {/* Error */}
      {error && (
        <div className="rounded-lg bg-red-50 border border-red-200 px-4 py-3 text-sm text-red-700">
          Impossible de charger les données cash-flow : {error.message}
        </div>
      )}

      {/* Loading */}
      {isLoading && (
        <div className="flex justify-center py-12">
          <LoadingSpinner />
        </div>
      )}

      {/* Empty state */}
      {isEmpty && (
        <div className="rounded-xl bg-slate-50 border border-slate-200 px-6 py-12 text-center">
          <p className="text-sm font-medium text-slate-700 mb-1">Aucune donnée sur cette période</p>
          <p className="text-sm text-slate-500">
            Les projections apparaîtront dès qu'une récurrence sera confirmée.
          </p>
        </div>
      )}

      {/* KPIs, graphique, dettes — après le tableau en paysage téléphone */}
      <div className={`flex flex-col gap-5 phonels:order-2 ${isPhonePortrait ? 'order-2' : ''}`}>
        {/* KPIs */}
        {!isLoading && buckets.length > 0 && (
          <CashFlowHeaderKPIs buckets={buckets} loading={isLoading} compact={isPhonePortrait} />
        )}

        {/* Chart */}
        {!isLoading && buckets.length > 0 && (
          <div className="bg-white rounded-xl border border-slate-200 shadow-sm px-4 py-4">
            <h2 className="text-sm font-semibold text-slate-800 mb-3">Flux et solde</h2>
            <CashFlowChart
              data={buckets}
              today={today}
              granularity={granularity}
              viewMode={viewMode}
              provisionCount={activeProvisionCount}
              height={isPhonePortrait ? 220 : 320}
            />
          </div>
        )}

        {/* Debts widget */}
        {!isLoading && <DebtsSummaryWidget />}

      </div>

      {/* Table */}
      {!isLoading && byCategory.length > 0 && (
        <div className={`phonels:order-1 ${isPhonePortrait ? 'order-1' : ''}`}>
          <h2 className="text-sm font-semibold text-slate-800 mb-3">Réel vs Budget par catégorie</h2>
          {isPhonePortrait ? (
            <CashFlowMonthView
              byCategory={byCategory}
              buckets={buckets}
              viewMode={viewMode}
              today={today}
              onCellClick={handleCellClick}
              onVatCellClick={handleVatCellClick}
              cashFlowData={data}
            />
          ) : (
            <ActualVsBudgetTable
              byCategory={byCategory}
              buckets={buckets}
              onCellClick={handleCellClick}
              onVatCellClick={handleVatCellClick}
              editing={editingTable}
              onEditingChange={setEditingTable}
              cashFlowData={data}
            />
          )}
        </div>
      )}

      {/* Drill-down panel (transactions passées ou forecasts selon le mode) */}
      <PeriodCategoryPanel
        open={!!cellDetail}
        onClose={handlePanelClose}
        title={cellDetail?.title ?? ''}
        subtitle={cellDetail?.subtitle ?? ''}
        categoryIds={cellDetail?.categoryIds ?? []}
        from={cellDetail?.from}
        to={cellDetail?.to}
        mode={cellDetail?.mode ?? 'actuals'}
        flowType={cellDetail?.flowType ?? 'expense'}
        scenario={cellDetail?.scenario ?? scenario}
        unclassified={cellDetail?.unclassified ?? false}
        vatFilter={cellDetail?.vatFilter ?? null}
      />

      {/* Modal admin snapshots */}
      <SnapshotAdminModal
        open={snapshotModalOpen}
        onClose={() => setSnapshotModalOpen(false)}
      />
    </div>
  );
}
